/**
 * HTTP client using native fetch - works on iOS without CORS issues.
 *
 * IMPORTANT: On iOS WKWebView and some PWA environments, reading `response.body`
 * can fail with "Body is disturbed or locked" if the body stream was already
 * consumed by the platform / a service worker. We defensively clone the response
 * before reading and fall back to `.text()` if `.json()` fails.
 */

async function safeReadJson(response) {
  // Read the body ONCE as text, then try to parse as JSON.
  // We deliberately avoid `response.clone()` because on iOS WKWebView
  // (and some Service Worker configurations) `clone()` can throw
  // "Body is disturbed or locked" when the body stream was briefly
  // touched by the platform — which would abort before our try/catch.
  try {
    const txt = await response.text();
    if (!txt) return {};
    try {
      return JSON.parse(txt);
    } catch {
      // Not JSON — return raw text under `detail` so error handlers still work
      return { detail: txt };
    }
  } catch (e) {
    // Absolute last-resort: body was unreadable. Status is still available.
    console.warn('[httpClient] body read failed:', e && e.message);
    return {};
  }
}

// User-friendly fallback messages by HTTP status — used when the response
// body is empty/unreadable (iOS WKWebView quirks) so the user never sees
// "Request failed (401)" and similar technical noise. Note that callers
// who actually KNOW the auth context (e.g. login forms) should override
// this fallback with their own copy — for everyone else, "session
// expired" is the truthful, generic interpretation of a bare 401/403.
function defaultMessageFor(status) {
  if (status === 401) return 'Sessione scaduta. Accedi di nuovo.';
  if (status === 403) return 'Operazione non autorizzata.';
  if (status === 404) return 'Risorsa non trovata';
  if (status === 409) return 'Operazione non permessa';
  if (status === 422) return 'Dati non validi';
  if (status === 429) return 'Troppi tentativi. Riprova tra qualche minuto.';
  if (status >= 500) return 'Errore del server. Riprova tra poco.';
  return `Errore ${status}`;
}

// Get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem('session_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// Universal language header — backend reads this on every request and
// keeps `users.language` in sync with the actual UI language.
const getLangHeader = () => {
  const lang = localStorage.getItem('app_language');
  return lang ? { 'X-App-Language': lang } : {};
};

// Universal platform header — populated from Capacitor.getPlatform()
// when available (ios/android), falls back to 'web' otherwise. Cached
// on the module to avoid re-importing Capacitor on every request.
let _cachedPlatform = null;
const getPlatformHeader = () => {
  if (_cachedPlatform === null) {
    try {
      const cap = typeof window !== 'undefined' ? window.Capacitor : null;
      _cachedPlatform = cap?.getPlatform?.() || 'web';
    } catch {
      _cachedPlatform = 'web';
    }
  }
  return { 'X-Slimmy-Platform': _cachedPlatform };
};

export const httpClient = {
  async get(url, options = {}) {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...getLangHeader(),
        ...getPlatformHeader(),
        ...options.headers,
      },
    });
    const data = await safeReadJson(response);
    return { data, status: response.status };
  },

  async post(url, body, options = {}) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...getLangHeader(),
        ...getPlatformHeader(),
        ...options.headers,
      },
      body: JSON.stringify(body),
    });
    const data = await safeReadJson(response);
    if (!response.ok) {
      const error = new Error(data.detail || defaultMessageFor(response.status));
      error.response = { data, status: response.status };
      throw error;
    }
    return { data, status: response.status };
  },

  async put(url, body, options = {}) {
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...getLangHeader(),
        ...getPlatformHeader(),
        ...options.headers,
      },
      body: JSON.stringify(body),
    });
    const data = await safeReadJson(response);
    if (!response.ok) {
      const error = new Error(data.detail || defaultMessageFor(response.status));
      error.response = { data, status: response.status };
      throw error;
    }
    return { data, status: response.status };
  },

  async delete(url, options = {}) {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...getLangHeader(),
        ...getPlatformHeader(),
        ...options.headers,
      },
    });
    const data = await safeReadJson(response);
    if (!response.ok) {
      const error = new Error(data.detail || defaultMessageFor(response.status));
      error.response = { data, status: response.status };
      throw error;
    }
    return { data, status: response.status };
  },
};

export default httpClient;
